import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, IconButton, Badge } from '@mui/material';
import { BellIcon } from '../../../icons/animated';
import { NAVBAR_MENU_KEYS, NS } from '../../../i18n/constants/keys';

const NOTIFICATION_KEYS: readonly string[] = [
  NAVBAR_MENU_KEYS.notifications.welcome,
  NAVBAR_MENU_KEYS.notifications.assessmentReady,
  NAVBAR_MENU_KEYS.notifications.newApplication,
];

export const Notifications: React.FC = () => {
  const { t } = useTranslation(NS.navbarMenus);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box sx={{ position: 'relative' }} ref={menuRef}>
      <IconButton
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMenuOpen(!menuOpen);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        size="small"
        sx={{
          p: 0.75,
          borderRadius: '8px',
          transition: 'background-color 0.2s',
          '&:hover': {
            bgcolor: 'var(--navbar-btn-hover, rgba(255, 255, 255, 0.1))',
          },
        }}
        aria-label="Notifications"
      >
        <Badge
          badgeContent={NOTIFICATION_KEYS.length}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '10px',
              height: 16,
              minWidth: 16,
              p: '0 4px',
            },
          }}
        >
          <BellIcon isHovered={isHovered} size={22} color="var(--navbar-text, #ffffff)" />
        </Badge>
      </IconButton>

      {menuOpen && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            p: 1,
            right: 0,
            mt: 1,
            width: 224,
            bgcolor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            maxHeight: 400,
            overflowY: 'auto',
            zIndex: 9999,
          }}
        >
          {NOTIFICATION_KEYS.length === 0 ? (
            <Typography variant="body2" sx={{ textAlign: 'center', color: '#6b7280', p: 1 }}>
              {t(NAVBAR_MENU_KEYS.notifications.empty)}
            </Typography>
          ) : (
            NOTIFICATION_KEYS.map((notificationKey) => (
              <Box
                key={notificationKey}
                sx={{
                  p: 1.25,
                  borderBottom: '1px solid #f3f4f6',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: '#f9fafb' },
                  borderRadius: '6px',
                  transition: 'background-color 0.15s',
                }}
              >
                <Typography variant="caption" sx={{ color: '#374151', fontWeight: 500, display: 'block' }}>
                  {t(notificationKey)}
                </Typography>
              </Box>
            ))
          )}
        </Paper>
      )}
    </Box>
  );
};

export default Notifications;
